import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  Button,
  CircularProgress,
  alpha,
  Link,
  Alert,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Flag as FlagIcon,
  PriorityHigh as PriorityHighIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Launch as LaunchIcon,
  AttachFile as AttachFileIcon,
  Description as DescriptionIcon,
  Visibility as VisibilityIcon,
  InsertDriveFile as InsertDriveFileIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import AuditService from '../../services/AuditService';
import { useSnackbar } from 'notistack';

const WorkpapersEvidence = ({ workpaperId, projectID }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [evidenceFiles, setEvidenceFiles] = useState({});  // Changed to object to store files by request ID
  const [loadingEvidence, setLoadingEvidence] = useState({});  // Changed to object to track loading by request ID
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const fetchEvidenceRequests = async () => {
    if (!workpaperId) {
      setError('Workpaper ID is required');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await AuditService.fetchAuditRequestsByWorkpaper(workpaperId);
      setRequests(response);
    } catch (error) {
      console.error('Error fetching audit requests:', error);
      setError(error.response?.data?.detail || 'Failed to fetch audit requests. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (workpaperId) {
      fetchEvidenceRequests();
    }
  }, [workpaperId]);

  const handleRequestClick = (requestId) => {
    const url = `/audit/project/${projectID}/requests/${requestId}`;
    window.open(url, '_blank');
  };

  const handleDownloadEvidence = async (fileId) => {
    try {
      setLoading(true);
      await AuditService.downloadRequestEvidence(fileId);
      enqueueSnackbar('File downloaded successfully', { variant: 'success' });
    } catch (error) {
      console.error('Error downloading evidence:', error);
      enqueueSnackbar('Failed to download file', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const statusColors = {
      'Pending': '#FFA500',
      'In Review': '#3498db',
      'Completed': '#2ecc71',
      'Evidence Submitted': '#9b59b6',
      'Resubmission Needed': '#e74c3c'
    };
    return statusColors[status] || '#777';
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'High':
        return <ArrowUpwardIcon sx={{ color: '#e74c3c' }} />;
      case 'Medium':
        return <FlagIcon sx={{ color: '#f39c12' }} />;
      case 'Low':
        return <ArrowDownwardIcon sx={{ color: '#27ae60' }} />;
      default:
        return null;
    }
  };

  const handleAttachmentClick = (event, request) => {
    setSelectedRequest(request);
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRequest(null);
  };

  const handleViewAll = () => {
    setDialogOpen(true);
    // Keep the menu state while dialog is open
    // handleMenuClose();
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    handleMenuClose();
  };

  const handleDownloadSingle = async (evidenceFile) => {
    try {
      setLoading(true);
      console.log('Downloading file:', evidenceFile);
      
      const response = await AuditService.downloadEvidence(evidenceFile);
      
      // Check if we have a valid response
      if (!response || !response.data) {
        throw new Error('No data received from server');
      }

      // Get content type from the file itself
      const contentType = evidenceFile.file_type || 'application/octet-stream';
      
      // Create blob from response data with proper content type
      const blob = new Blob([response.data], { type: contentType });
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', evidenceFile.original_filename);
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      enqueueSnackbar('File downloaded successfully', { variant: 'success' });
    } catch (error) {
      console.error('Error downloading file:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to download file';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setLoading(false);
      handleMenuClose();
    }
  };

  const handleDownloadAll = async () => {
    try {
      setLoading(true);
      const response = await AuditService.downloadAllRequestEvidence(selectedRequest.id);
      
      // Create a blob from the response data
      const blob = new Blob([response.data], { type: 'application/zip' });
      
      // Create a temporary URL for the blob
      const url = window.URL.createObjectURL(blob);
      
      // Create a temporary link element
      const link = document.createElement('a');
      link.href = url;
      link.download = `evidence_${selectedRequest.REQUEST_ID}.zip`;
      
      // Append link to body, click it, and remove it
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the URL
      window.URL.revokeObjectURL(url);
      
      enqueueSnackbar('Files downloaded successfully', { variant: 'success' });
    } catch (error) {
      console.error('Error downloading files:', error);
      enqueueSnackbar('Failed to download files', { variant: 'error' });
    } finally {
      setLoading(false);
      handleDialogClose();
    }
  };

  // Helper function to format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {loading ? (
        <CircularProgress />
      ) : (
        <div>
          <Box sx={{ width: '100%', mb: 2 }}>
            <Paper sx={{ width: '100%', mb: 2 }}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Request ID</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Priority</TableCell>
                      <TableCell>Due Date</TableCell>
                      <TableCell>Evidence</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {requests.map((request) => (
                      <TableRow key={request.id}>
                        <TableCell>
                          <Link
                            component="button"
                            variant="body2"
                            onClick={() => handleRequestClick(request.id)}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              color: 'primary.main',
                              textDecoration: 'none',
                              '&:hover': {
                                textDecoration: 'underline',
                              },
                            }}
                          >
                            {request.REQUEST_ID}
                            <LaunchIcon sx={{ ml: 0.5, fontSize: 16 }} />
                          </Link>
                        </TableCell>
                        <TableCell>{request.REQUEST_TYPE}</TableCell>
                        <TableCell>{request.DESCRIPTION}</TableCell>
                        <TableCell>
                          <Chip 
                            label={request.STATUS}
                            sx={{
                              backgroundColor: alpha(getStatusColor(request.STATUS), 0.1),
                              color: getStatusColor(request.STATUS),
                              borderColor: getStatusColor(request.STATUS),
                              border: '1px solid'
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Tooltip title={request.PRIORITY}>
                            <Box display="flex" alignItems="center">
                              {getPriorityIcon(request.PRIORITY)}
                              <Typography variant="body2" sx={{ ml: 1 }}>
                                {request.PRIORITY}
                              </Typography>
                            </Box>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          {new Date(request.DUE_DATE).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          {request.evidence_files && request.evidence_files.length > 0 && (
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Tooltip title={`View Evidence (${request.evidence_files.length} files)`}>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    color: 'primary.main',
                                    cursor: 'pointer',
                                    '&:hover': {
                                      opacity: 0.8
                                    }
                                  }}
                                  onClick={(e) => handleAttachmentClick(e, request)}
                                >
                                  <AttachFileIcon sx={{ fontSize: 16, mr: 0.5 }} />
                                  <Typography variant="body2" component="span">
                                    {request.evidence_files.length}
                                  </Typography>
                                </Box>
                              </Tooltip>
                            </Stack>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Box>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            {selectedRequest && selectedRequest.evidence_files?.map((file) => (
              <MenuItem key={file.id} onClick={() => handleDownloadSingle(file)}>
                <ListItemIcon>
                  <DescriptionIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText 
                  primary={file.original_filename || file.file_name} 
                  secondary={`Uploaded: ${new Date(file.upload_date).toLocaleDateString()}`}
                />
              </MenuItem>
            ))}
            {selectedRequest && selectedRequest.evidence_files?.length > 0 && (
              <MenuItem onClick={handleViewAll}>
                <ListItemIcon>
                  <DescriptionIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText 
                  primary="View All Files" 
                  secondary={`${selectedRequest.evidence_files.length} files total`}
                />
              </MenuItem>
            )}
          </Menu>

          <Dialog
            open={dialogOpen}
            onClose={handleDialogClose}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h6">
                  Evidence Files 
                  {selectedRequest && (
                    <Typography component="span" variant="subtitle2" sx={{ ml: 1 }}>
                      ({selectedRequest.evidence_files?.length || 0} files)
                    </Typography>
                  )}
                </Typography>
                <Button
                  onClick={handleDownloadAll}
                  startIcon={<DownloadIcon />}
                  variant="contained"
                  color="primary"
                  size="small"
                >
                  Download All
                </Button>
              </Box>
            </DialogTitle>
            <DialogContent>
              {selectedRequest ? (
                selectedRequest.evidence_files?.length > 0 ? (
                  <List>
                    {selectedRequest.evidence_files.map((file) => (
                      <ListItem
                        key={file.id}
                        divider
                        secondaryAction={
                          <IconButton 
                            edge="end" 
                            onClick={() => handleDownloadSingle(file)}
                            title="Download file"
                          >
                            <DownloadIcon />
                          </IconButton>
                        }
                      >
                        <ListItemIcon>
                          <DescriptionIcon />
                        </ListItemIcon>
                        <ListItemText
                          primary={file.original_filename || file.file_name}
                          secondary={
                            <Typography variant="body2" color="textSecondary">
                              Size: {(file.file_size / 1024).toFixed(2)} KB • 
                              Uploaded: {new Date(file.upload_date).toLocaleDateString()} • 
                              By: {file.uploaded_by || 'Unknown'}
                            </Typography>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Box display="flex" justifyContent="center" alignItems="center" p={3}>
                    <Typography color="textSecondary">No files available</Typography>
                  </Box>
                )
              ) : (
                <Box display="flex" justifyContent="center" alignItems="center" p={3}>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  <Typography>Loading files...</Typography>
                </Box>
              )}
            </DialogContent>
          </Dialog>
        </div>
      )}
    </Box>
  );
};

export default WorkpapersEvidence;
